import { apiClient } from '../client';
import { stockRowsFromRules } from '../products/availabilityRules';
import type { ApiStock } from '../products/types';
import type { PresetMenuRow } from '@components/PresetMenuManagement/columns';
import type {
  PresetMenuDetailView,
  PresetMenuItemRow,
} from '@components/PresetMenuDetails/types';
import type {
  ApiPresetMenu,
  ApiPresetMenuDetail,
  ApiPresetMenuItem,
  PresetMenusListResponse,
  PresetMenusListParams,
  PresetMenuDetailResponse,
} from './types';

export const presetMenusQueryKeys = {
  all: ['presetMenus'] as const,
  lists: () => [...presetMenusQueryKeys.all, 'list'] as const,
  list: (params?: PresetMenusListParams) => [...presetMenusQueryKeys.lists(), params] as const,
  details: () => [...presetMenusQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...presetMenusQueryKeys.details(), id] as const,
};

export async function getPresetMenus(
  params?: PresetMenusListParams,
): Promise<PresetMenusListResponse> {
  const response = await apiClient.get<PresetMenusListResponse>('/api/preset_menus', { params });
  return response.data;
}

export async function getPresetMenu(id: number): Promise<PresetMenuDetailResponse> {
  const response = await apiClient.get<PresetMenuDetailResponse>(`/api/preset_menus/${id}`);
  return response.data;
}

// Earliest start / latest end across a product's stocks (nulls ignored).
// Mirrors formatStocksRange in api/products/queries.ts.
function stocksDateRange(stocks: ApiStock[]): { start: string | null; end: string | null } {
  const starts = stocks.map((s) => s.start_date).filter((d): d is string => d != null);
  const ends = stocks.map((s) => s.end_date).filter((d): d is string => d != null);
  return {
    start: starts.length > 0 ? starts.reduce((min, d) => (d < min ? d : min)) : null,
    end: ends.length > 0 ? ends.reduce((max, d) => (d > max ? d : max)) : null,
  };
}

export function mapApiPresetMenuItemToRow(api: ApiPresetMenuItem): PresetMenuItemRow {
  const product = api.item;
  const stocks = stocksDateRange(stockRowsFromRules(product.availability_rules));

  return {
    id: api.id,
    position: api.position,
    product_id: product.id,
    name: product.name,
    form: product.form,
    category: product.category,
    active_status: product.active_status,
    serving_period_start: product.serving_period?.start_date ?? null,
    serving_period_end: product.serving_period?.end_date ?? null,
    diet_tags: product.diet_tags,
    nutrition_tags: product.nutrition_tags,
    allergen_tags: product.allergen_tags,
    chef_recommended: product.chef_recommended,
    stocks_start: stocks.start,
    stocks_end: stocks.end,
    image_url: product.images[0]?.url ?? null,
  };
}

// Rebind `form` → `format` and `owner.name` → `creator_name` (same seam as the
// list mapper), and flatten items into rows. The API already returns items in
// position order; client-side column sorting happens in the component.
export function mapApiPresetMenuDetail(api: ApiPresetMenuDetail): PresetMenuDetailView {
  return {
    id: api.id,
    name: api.name,
    format: api.form,
    product_line: api.product_line,
    price_per_pax: api.price_per_pax,
    active: api.active,
    created_at: api.created_at,
    creator_name: api.owner?.name ?? null,
    items: api.items.map(mapApiPresetMenuItemToRow),
  };
}

// Single seam where the API key `form` becomes the UI key `format`.
// Owner is reshaped to the {id, name, avatar_url} subset the table column
// renders. Don't fall back name → email; the column is labelled "Owner"
// and email is PII that shouldn't be silently surfaced.
export function mapApiPresetMenuToRow(api: ApiPresetMenu): PresetMenuRow {
  return {
    id: api.id,
    name: api.name,
    format: api.form ?? '',
    product_line: api.product_line ?? '',
    price_per_pax: api.price_per_pax,
    owner: api.owner
      ? { id: api.owner.id, name: api.owner.name, avatar_url: api.owner.avatar_url }
      : null,
    active: api.active,
  };
}
